/**
 * `/pr` — Peter's PR pipeline (2026-07-19).
 *
 * Supersedes `/outreach`. Three sections, in the order Mario asked for them:
 *
 *   (e) Overview  — contacted / replied / agreed / sessions / delivered, studio-wide.
 *   (a) Pipeline  — every collaboration grouped by stage, one TABLE per stage,
 *                   each row carrying its full per-stage timestamp strip. It
 *                   was a card grid until 2026-08-06; the cards spent about ten
 *                   lines on the eight facts a row states in two, and this board
 *                   is read by scanning many collaborations at once.
 *   (c) People    — journalists / influencers / local partners; each links to
 *                   `/pr/people/:id`, which owns the cross-channel thread.
 *
 * Deliberately simpler than `/tasks`: no 6-field charter, no onboarding state,
 * no group/solo split, no pinning — the four things that made the task board
 * heavy. An engagement is person + angle + exchange kind, and it is always
 * implicitly Mario's, so there is no assignee to render either.
 *
 * Read-only. Every mutation goes through the `create_pr_engagement` /
 * `record_pr_engagement_event` canonical actions that Peter drives, or through
 * the reply composer on the person's profile. One aggregate fetch,
 * `GET /api/pr-board`.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  api,
  ApiError,
  type PrBoardPerson,
  type PrBoardResponse,
  type PrEngagement,
} from "../lib/api";
import { ProgressStrip } from "../components/ProgressStrip";
import { ActionModalButton } from "../components/ActionModalButton";
import { PendingApprovalsPanel } from "../components/PendingApprovalsPanel";
import { pipelineOutcome } from "../lib/pipeline-outcome";
import {
  PR_LOG_EVENT_KINDS,
  stageOptionsAfter,
  validatePrEventDraft,
} from "../lib/pr-engagement-edit";
import { studioFormat } from "../lib/studio-tz";
import {
  EMPTY_PR_FILTERS,
  emailEventDisplay,
  exchangeLabel,
  filterPeople,
  filtersActive,
  PR_TYPE_TABS,
  progressSteps,
  stageLabel,
  statusLabel,
  typeBadge,
  type PrPeopleFilters,
} from "../lib/pr-board";

const DATE_OPTS: Intl.DateTimeFormatOptions = { month: "short", day: "2-digit" };
const DATETIME_OPTS: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};

/** Studio-time, never browser-local — the studio is in London, Mario is not always. */
function fmt(iso: string | null, opts: Intl.DateTimeFormatOptions): string {
  if (iso === null) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : studioFormat(opts).format(d);
}

const fmtDate = (iso: string | null): string => fmt(iso, DATE_OPTS);
const fmtDateTime = (iso: string | null): string => fmt(iso, DATETIME_OPTS);

export function Pr(): JSX.Element {
  const [data, setData] = useState<PrBoardResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [filters, setFilters] = useState<PrPeopleFilters>(EMPTY_PR_FILTERS);
  const [showClosed, setShowClosed] = useState<boolean>(false);
  const navigate = useNavigate();

  /**
   * Lifted out of the mount effect so a row control can refetch after it
   * mutates. `useDispatch`'s automatic `invalidateAfterMutation()` does NOT
   * reach this board — it busts the `resource-cache` keys and this page fetches
   * with a plain effect — so without an explicit refetch every button would
   * appear to do nothing.
   */
  const loadBoard = useCallback((): void => {
    // Deliberately does NOT set `loading` — a REFETCH must not blank the page.
    // The component early-returns "Loading PR…" while `loading` is true, which
    // unmounts everything including any open action modal; Reconcile's findings
    // (rendered via `renderSuccess`, so the modal is meant to STAY open) were
    // destroyed the instant the refetch began. The first load still shows the
    // spinner because `loading` starts true and is cleared here.
    api
      .prBoard()
      .then((r) => {
        setData(r);
        setError(null);
      })
      .catch((e: Error) => {
        setError(
          e instanceof ApiError ? (e.code ?? `http_${e.status}`) : e.message,
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    loadBoard();
  }, [loadBoard]);

  // Closed work is fetched but hidden by default — it is history, not a queue.
  const visibleGroups = useMemo(
    () =>
      data === null
        ? []
        : data.groups.filter((g) => showClosed || g.stage !== "closed"),
    [data, showClosed],
  );

  const visiblePeople = useMemo(
    () => (data === null ? [] : filterPeople(data.people, filters)),
    [data, filters],
  );

  if (loading) return <p className="empty">Loading PR…</p>;
  if (error !== null) {
    return <p className="empty">Couldn&apos;t load PR ({error}).</p>;
  }
  if (data === null) return <p className="empty">No data.</p>;

  const c = data.counts;
  const closedCount = c.by_stage.closed;

  return (
    <div>
      <h1 className="page-title">PR</h1>

      {/*
        (e) Overview — studio-wide, deliberately ignores every filter below.
        These are the five the brief asked for, in its words: contacted, replied,
        did a session, brought someone, started a partnership. "Brought someone"
        is a delivered paying_plus_one (they came and paid for a guest); a
        partnership is a delivered local_partner. Agreed and Delivered are NOT
        shown: they are real but secondary, and the row reads as five headline
        numbers rather than five plus a footnote. `counts.agreed` /
        `counts.delivered` (and the per-exchange-kind breakdown) still come back
        from the board if they are ever wanted again.

        Every figure here is counted from TIMESTAMPS, not the current stage — so
        someone who replied and later closed still counts as replied, and the
        totals never go down as the pipeline moves. That used to be spelled out
        under the row; it is kept here because it is a real property of the
        numbers, and the next person to wonder why Contacted exceeds the live
        stage counts will be reading this file, not the page.
      */}
      <div className="pr-overview">
        <OverviewStat label="Contacted" value={c.contacted} />
        <OverviewStat label="Replied" value={c.replied} />
        <OverviewStat label="Sessions" value={c.sessions_done} />
        <OverviewStat
          label="Brought someone"
          value={c.delivered_by_exchange_kind.paying_plus_one}
        />
        <OverviewStat label="Partnerships" value={c.partnerships} />
      </div>

      {/* Approvals FIRST — above the pipeline. A staged send is the only thing
          on this page that is waiting on a human, and everything below it is
          reference. It runs alongside the Telegram card, never instead of it. */}
      <PendingApprovalsPanel
        lane="peter"
        drain={data.drain ?? null}
        onResolved={loadBoard}
      />

      {/* (a) Pipeline — one section per stage, in enum (= pipeline) order. */}
      <div className="pr-section-head">
        <h2 className="pr-heading">Pipeline</h2>
        {closedCount > 0 ? (
          <button
            type="button"
            className="pr-toggle"
            onClick={() => setShowClosed((v) => !v)}
          >
            {showClosed ? "Hide" : "Show"} closed ({closedCount})
          </button>
        ) : null}
        {/* Reconcile is a REPORT with a repair arm — it is the one PR action
            whose value is its findings, so the modal stays open to show them
            (`renderSuccess`). There is no inbox twin: `reconcile_inbox_board`
            does not admit human:staff. */}
        <ActionModalButton
          action="reconcile_pr_board"
          buttonLabel="Reconcile board"
          buttonClassName="pr-toggle"
          buttonTitle="Check the board for drift between stages, stamps and people"
          modalTitle="Reconcile the PR board"
          description="Looks for stage/stamp drift, engagements without a timeline, and closed rows missing a reason."
          submitLabel="Reconcile"
          renderSuccess={(r) => <ReconcileFindings response={r} />}
          onDone={loadBoard}
        />
      </div>

      {visibleGroups.length === 0 ? (
        <p className="empty">
          {c.total === 0
            ? "No PR collaborations yet — Peter opens one when it drafts a pitch."
            : "Nothing live. Every collaboration is closed."}
        </p>
      ) : (
        visibleGroups.map((g) => (
          <section key={g.stage} className="pr-stage">
            <h3 className="pr-stage-title">
              {stageLabel(g.stage)}
              <span className="dc-muted"> · {g.engagements.length}</span>
            </h3>
            <div className="table-scroll-x">
              <table className="pr-people-table table-cards">
                <thead>
                  <tr>
                    <th>Contact</th>
                    <th>Type</th>
                    <th>Outlet</th>
                    <th>Angle</th>
                    <th>Asking for</th>
                    <th>Progress</th>
                    <th>Next</th>
                    <th>Last event</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {g.engagements.map((e) => (
                    <EngagementRow
                      key={e.pr_engagement_id}
                      engagement={e}
                      navigate={navigate}
                      onChanged={loadBoard}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))
      )}
      {data.has_more ? (
        <p className="subtle">
          Showing the first {data.groups.reduce((n, g) => n + g.engagements.length, 0)}{" "}
          collaborations — older closed ones are not listed.
        </p>
      ) : null}

      {/* (c) People — the contacts themselves. */}
      <div className="pr-section-head">
        <h2 className="pr-heading">People</h2>
        <span className="subtle">
          {data.people.length} contact{data.people.length === 1 ? "" : "s"}
          {filtersActive(filters) ? ` · showing ${visiblePeople.length}` : ""}
        </span>
      </div>

      <div className="pr-filters">
        <div className="pr-type-tabs" role="tablist" aria-label="Contact type">
          {PR_TYPE_TABS.map((t) => (
            <button
              key={t.value}
              type="button"
              role="tab"
              aria-selected={filters.type === t.value}
              className={`pr-type-tab${filters.type === t.value ? " active" : ""}`}
              onClick={() => setFilters((f) => ({ ...f, type: t.value }))}
            >
              {t.label}
            </button>
          ))}
        </div>
        <input
          type="search"
          className="pr-search"
          placeholder="Search name / outlet / niche / angle…"
          value={filters.query}
          onChange={(e) => setFilters((f) => ({ ...f, query: e.target.value }))}
        />
        <label className="pr-minscore">
          Min score
          <input
            type="number"
            min={0}
            max={100}
            value={filters.minScore ?? ""}
            onChange={(e) =>
              setFilters((f) => ({
                ...f,
                minScore:
                  e.target.value === ""
                    ? null
                    : Number.parseInt(e.target.value, 10),
              }))
            }
          />
        </label>
      </div>

      {visiblePeople.length === 0 ? (
        <p className="empty">
          {data.people.length === 0
            ? "No PR contacts yet — Peter adds them as it researches."
            : "No contacts match these filters."}
        </p>
      ) : (
        <div className="table-scroll-x">
          <table className="pr-people-table table-cards">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Outlet / Niche</th>
                <th className="pr-num">Score</th>
                <th>Status</th>
                <th>Collaboration</th>
                <th>Last contact</th>
                <th>Last reply</th>
                <th>Engagement</th>
              </tr>
            </thead>
            <tbody>
              {visiblePeople.map((p) => (
                <PersonRow key={p.id} person={p} navigate={navigate} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function OverviewStat({
  label,
  value,
}: {
  readonly label: string;
  readonly value: number;
}): JSX.Element {
  return (
    <div className="pr-stat">
      <div className="pr-stat-value">{value}</div>
      <div className="pr-stat-label">{label}</div>
    </div>
  );
}

/**
 * One collaboration. The timestamp strip is still the point of the row: every
 * stage it has passed, when, at a glance — alongside every other fact the card
 * this replaced carried (name, member flag, outlet, angle, what we asked for,
 * the session location, the follow-up stamp, the next action or the closing
 * reason, and the last event).
 */
/**
 * `reconcile_pr_board`'s findings, rendered inside the modal so they can be
 * read rather than flashing past. `detail` is non-PII by the action's contract
 * (a column name, a stage, or a status transition), so it renders verbatim.
 */
function ReconcileFindings({ response }: { readonly response: unknown }): JSX.Element {
  const r = response as {
    readonly repaired_count?: number;
    readonly needs_judgement_count?: number;
    readonly repaired?: ReadonlyArray<{ drift: string; detail: string }>;
    readonly needs_judgement?: ReadonlyArray<{ drift: string; detail: string }>;
  };
  const repaired = r.repaired ?? [];
  const needs = r.needs_judgement ?? [];
  return (
    <div>
      <p>
        Repaired <strong>{r.repaired_count ?? 0}</strong> · needs your judgement{" "}
        <strong>{r.needs_judgement_count ?? 0}</strong>
      </p>
      {repaired.length + needs.length === 0 ? (
        <p className="dc-muted">No drift found — the board agrees with itself.</p>
      ) : (
        <ul className="data-delete-deps">
          {repaired.map((d, i) => (
            <li key={`r${i}`}>
              <span className="tag tag-success">{d.drift}</span> {d.detail}
            </li>
          ))}
          {needs.map((d, i) => (
            <li key={`n${i}`}>
              <span className="tag tag-warn">{d.drift}</span> {d.detail}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function EngagementRow({
  engagement: e,
  navigate,
  onChanged,
}: {
  readonly engagement: PrEngagement;
  readonly navigate: ReturnType<typeof useNavigate>;
  /** Refetch the board after a row control mutates the collaboration. */
  readonly onChanged: () => void;
}): JSX.Element {
  // A PR engagement has no free-text next action, only a date — so the note
  // line under "Next" is populated for a CLOSED row (its reason) and empty
  // while it is live.
  const outcome = pipelineOutcome({
    stage: e.stage,
    closed_at: e.closed_at,
    closed_reason: e.closed_reason,
    next_action_at: e.next_action_at,
    next_action: null,
  });
  const to = `/pr/people/${encodeURIComponent(e.outreach_target_id)}`;
  return (
    <tr
      className="pr-row"
      onClick={() => navigate(to)}
      title="Open contact profile"
    >
      <td className="pr-cell-title table-card-title">
        {e.target_name}
        {e.target_member_id !== null ? (
          <span
            className="tag tag-error pr-member-flag"
            title="Already a member — never cold-pitch"
          >
            member
          </span>
        ) : null}
      </td>
      <td data-label="Type">
        <span className="pr-type-badge">{typeBadge(e.target_type)}</span>
      </td>
      <td data-label="Outlet">
        {e.target_company_outlet ?? <span className="dc-muted">—</span>}
      </td>
      <td data-label="Angle" className="pr-cell-prose">
        <div className="pr-clamp" title={e.angle}>
          {e.angle}
        </div>
      </td>
      <td data-label="Asking for" className="pr-cell-ask">
        {exchangeLabel(e.exchange_kind)}
        {e.session_location !== null ? (
          <div className="pr-sub dc-muted">{e.session_location} session</div>
        ) : null}
      </td>
      <td data-label="Progress">
        <ProgressStrip steps={progressSteps(e)} />
        {e.followed_up_at !== null ? (
          <div className="pr-sub dc-muted">
            Followed up {fmtDate(e.followed_up_at)}
          </div>
        ) : null}
      </td>
      <td data-label="Next" className="pr-cell-next">
        {outcome.empty ? (
          <span className="dc-muted">—</span>
        ) : (
          <>
            <div>
              {outcome.label} {fmtDate(outcome.at)}
            </div>
            {outcome.note !== null ? (
              <div className="pr-sub dc-muted pr-clamp" title={outcome.note}>
                {outcome.note}
              </div>
            ) : null}
          </>
        )}
      </td>
      <td data-label="Last event" className="pr-cell-prose">
        {e.last_event === null ? (
          <span className="dc-muted">—</span>
        ) : (
          <>
            <div className="pr-clamp" title={e.last_event.summary}>
              {e.last_event.summary}
            </div>
            <div className="pr-sub dc-muted">
              {fmtDateTime(e.last_event.at)}
            </div>
          </>
        )}
      </td>
      {/* Row actions. The whole <tr> navigates to the CONTACT, so this cell
          must stop the click propagating (the /data row-delete precedent).
          The stage picker inside offers only this stage forward and never
          `closed` — see `stageOptionsAfter`. */}
      <td
        className="data-td-actions"
        onClick={(ev) => ev.stopPropagation()}
        role="presentation"
      >
        {e.stage !== "closed" ? (
          <ActionModalButton
            action="record_pr_engagement_event"
            buttonLabel="Log"
            buttonClassName="row-action-btn"
            buttonTitle="Append an event, and optionally move the stage forward"
            modalTitle="Log an event"
            description={e.angle}
            prefill={{ pr_engagement_id: e.pr_engagement_id }}
            fields={[
              {
                kind: "select",
                name: "kind",
                label: "Kind",
                required: true,
                defaultValue: "note",
                options: PR_LOG_EVENT_KINDS.map((k) => ({
                  value: k,
                  label: k,
                })),
              },
              {
                kind: "textarea",
                name: "summary",
                label: "Summary",
                required: true,
                maxLength: 500,
              },
              {
                kind: "select",
                name: "stage",
                label: "Move to stage (optional)",
                options: stageOptionsAfter(e.stage).map((s) => ({
                  value: s,
                  label: s,
                })),
              },
              { kind: "datetime", name: "at", label: "When (defaults to now)" },
              {
                kind: "datetime",
                name: "next_action_at",
                label: "Next action",
              },
            ]}
            validate={validatePrEventDraft}
            submitLabel="Log it"
            onDone={onChanged}
          />
        ) : null}
      </td>
    </tr>
  );
}

function PersonRow({
  person: p,
  navigate,
}: {
  readonly person: PrBoardPerson;
  readonly navigate: ReturnType<typeof useNavigate>;
}): JSX.Element {
  const to = `/pr/people/${encodeURIComponent(p.id)}`;
  const outlet = p.company_outlet ?? "";
  const niche = p.niche ?? "";
  const engagement =
    p.last_email_event === null
      ? null
      : emailEventDisplay(p.last_email_event.type);
  const lastContact =
    p.contact_date === null
      ? "—"
      : `${fmtDate(p.contact_date)}${p.channel_used !== null ? ` · ${p.channel_used}` : ""}`;
  // Directional split (2026-07-24, Mario's ask): "Last contact" is OUR last
  // outbound to them (contact_date); "Last reply" is THEIR last inbound to us
  // (response_date). One column can never stand in for the other.
  const lastReply = p.response_date === null ? "—" : fmtDate(p.response_date);
  return (
    <tr className="pr-row" onClick={() => navigate(to)} title="Open contact profile">
      <td className="pr-name table-card-title">
        {p.name}
        {p.member_id !== null ? (
          <span
            className="tag tag-error pr-member-flag"
            title="Already a member — never cold-pitch"
          >
            member
          </span>
        ) : null}
      </td>
      <td data-label="Type">
        <span className="pr-type-badge">{typeBadge(p.type)}</span>
      </td>
      <td data-label="Outlet / Niche">
        {outlet.length > 0 ? <span>{outlet}</span> : null}
        {outlet.length > 0 && niche.length > 0 ? (
          <span className="dc-muted"> · </span>
        ) : null}
        {niche.length > 0 ? <span className="dc-muted">{niche}</span> : null}
        {outlet.length === 0 && niche.length === 0 ? (
          <span className="dc-muted">—</span>
        ) : null}
      </td>
      <td data-label="Score" className="pr-num">
        {p.quality_score === null ? (
          <span className="dc-muted">—</span>
        ) : (
          <span className="pr-score">{p.quality_score}</span>
        )}
      </td>
      <td data-label="Status">
        <span className="tag">{statusLabel(p.status)}</span>
      </td>
      <td data-label="Collaboration">
        {p.live_engagement === null ? (
          <span className="dc-muted">—</span>
        ) : (
          <span className="pr-stage-chip">
            {stageLabel(p.live_engagement.stage)}
          </span>
        )}
      </td>
      <td data-label="Last contact" className="pr-lastcontact">{lastContact}</td>
      <td data-label="Last reply" className="pr-lastcontact">{lastReply}</td>
      <td data-label="Engagement">
        {engagement === null ? (
          <span className="dc-muted">—</span>
        ) : (
          <span
            className={`pr-engmt pr-engmt-${engagement.tone}`}
            title={`Latest email event · ${fmtDate(p.last_email_event?.at ?? null)}`}
          >
            {engagement.label}
          </span>
        )}
      </td>
    </tr>
  );
}
